import { useEffect, Suspense, lazy } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";
import { Helmet } from "react-helmet";
import Navbar from "./components/Navbar";
import Hero from "./components/Hero";
import About from "./components/About";
import Services from "./components/Services";
import SGEPreview from "./components/SGEPreview";
import ResultsSection from "./components/ResultsSection";
import Portfolio from "./components/Portfolio";
import Contact from "./components/Contact";
import Footer from "./components/Footer";
import GlobalParticles from "./components/GlobalParticles";

// Páginas de produto carregadas sob demanda (reduz o bundle inicial da home)
const SistemasPersonalizados = lazy(() => import("./components/pages/SistemasPersonalizados"));
const SGEPro = lazy(() => import("./components/pages/SGEPro"));
const BeautyAgenda = lazy(() => import("./components/pages/BeautyAgenda"));
const HemathClin = lazy(() => import("./components/pages/HemathClin"));
const Vitalis = lazy(() => import("./components/pages/Vitalis"));
const WhatsAppExtractor = lazy(() => import("./components/pages/WhatsAppExtractor"));
const ERE = lazy(() => import("./components/pages/ERE"));
const Dmarks = lazy(() => import("./components/pages/Dmarks"));
const Alicerce = lazy(() => import("./components/pages/Alicerce"));

// AOS só escaneia os elementos [data-aos] presentes no DOM quando AOS.init()
// roda. Trocar de página via react-router (sem recarregar o navegador) monta
// elementos novos que o AOS nunca viu — sem isto, eles ficam presos no
// estado inicial (invisíveis) nas páginas de produto (lazy-loaded).
//
// A Home ("/") não precisa disso — seus elementos já existem desde o
// AOS.init() inicial. Chamar refreshHard() também na Home (inclusive ao
// voltar pra ela vindo de outra rota) reseta e recalcula TODOS os
// elementos [data-aos] da página, e se isso rodar enquanto uma imagem
// ainda está carregando (layout mudando de altura), o recálculo pode
// achar um elemento já visto como "fora da viewport" e nunca reaplicar
// a classe que o deixa visível — o título fica preso invisível. Por
// isso o refresh só roda para rotas de produto, nunca para "/".
function AOSRouteRefresh() {
  const location = useLocation();
  useEffect(() => {
    const timer = setTimeout(() => AOS.refreshHard(), 250);
    return () => clearTimeout(timer);
  }, [location.pathname]);
  return null;
}

function App() {
  useEffect(() => {
    AOS.init({
      duration: 800,
      once: true,
      easing: 'ease-out-quad',
    });
  }, []);

  return (
    <Router>
      <AOSRouteRefresh />
      <GlobalParticles />
      <Navbar />

      <Routes>
        {/* Página principal (landing page) */}
        <Route
          path="/"
          element={
            <>
              <Helmet>
                <title>Henrimack | Engenharia de Software de Elite para Gestão de Eventos (SGE Pro)</title>
                <meta
                  name="description"
                  content="Henrimack Sistemas: engenharia de software de elite. Criadores do SGE Pro, sistema de gestão de eventos com mapa de reservas em tempo real, contratos automáticos e controle total de estoque."
                />
                <meta name="keywords" content="henrimack, sge pro, software para gestão de eventos, mapa de reservas de estandes, gestão de estoque, software sob medida, engenharia de software" />
                <meta property="og:title" content="Henrimack | SGE Pro — Gestão de Eventos de Elite" />
                <meta property="og:description" content="Mapa de reservas em tempo real, contratos automáticos e controle total de ativos para operações de eventos complexas." />
              </Helmet>
              <div id="hero"><Hero /></div>
              <div id="about"><About /></div>
              <Services />
              <SGEPreview />
              <ResultsSection />
              <Portfolio />
              <Contact />
            </>
          }
        />

        {/* Páginas individuais dos sistemas */}
        <Route
          path="/sistemas-personalizados"
          element={<Suspense fallback={null}><SistemasPersonalizados /></Suspense>}
        />
        <Route path="/sge-pro" element={<Suspense fallback={null}><SGEPro /></Suspense>} />
        <Route path="/beauty-agenda" element={<Suspense fallback={null}><BeautyAgenda /></Suspense>} />
        <Route path="/hemathclin" element={<Suspense fallback={null}><HemathClin /></Suspense>} />
        <Route path="/vitalis" element={<Suspense fallback={null}><Vitalis /></Suspense>} />
        <Route
          path="/whatsapp-extractor"
          element={<Suspense fallback={null}><WhatsAppExtractor /></Suspense>}
        />
        <Route path="/ere" element={<Suspense fallback={null}><ERE /></Suspense>} />
        <Route path="/dmarks" element={<Suspense fallback={null}><Dmarks /></Suspense>} />
        <Route path="/alicerce" element={<Suspense fallback={null}><Alicerce /></Suspense>} />
      </Routes>

      <Footer />
    </Router>
  );
}

export default App;
